<template lang="pug">
EditDealTab(id="online")
  EditDealTabSection(:full-width="false")
    template(v-slot:header="")
      .online-tab-header
        h2 Online
        BriqButton.translation-button(
          v-if="hasMultipleLanguages"
          small
          variant="secondary"
          @click="toggleTranslation"
          data-test-id="translation-button"
          ) 
          .flex
            .translation-icon 
              BriqIcon(:name="`icon-input-${toggleTranslationIcon}-icon`") 
            .translation-text {{ toggleTranslationText }}
    template(v-slot:body="")
      BriqMultilanguage(
        v-if="hasMultipleLanguages"
        v-model="activeCulture"
        :translationFields="translationFields"
        :cultures="availableCultures"
        :showSecondColumn="shouldShowMultilanguage"
        :nodesToHide="nodesToHide"
        :defaultCulture="defaultCultureDisplayName"
        )
        OnlineFields(
          :deal="deal" 
          :businessUnit="businessUnit" 
          :funnelBaseUrl="funnelBaseUrl" 
          :shouldShowMultilanguage="false" 
          :translationInfo="deal.TranslationInfo"
          )
        template(v-slot:secondary="")
          OnlineFields(
            v-if="activeCulture"
            :deal="translationModel"
            :businessUnit="businessUnit"
            :shouldShowMultilanguage="shouldShowMultilanguage"
            )
      OnlineFields(v-else :deal="deal" :businessUnit="businessUnit" :funnelBaseUrl="funnelBaseUrl")
</template>

<script>
import Vue from 'vue'
import EditDealTab from '@/vue/inventory/deals/edit/EditDealTab.vue'
import EditDealTabSection from '@/vue/inventory/deals/edit/EditDealTabSection.vue'
import OnlineFields from '@/vue/inventory/deals/edit/tab-online/OnlineFields.vue'
import BriqMultilanguage from '@/vue/components/BriqMultilanguage.vue'
import completeTranslationInfo from '@v/util/completeTranslationInfo.js'
import { clone } from 'ramda'

export default {
  name: 'OnlineTab',
  components: { EditDealTab, EditDealTabSection, OnlineFields, BriqMultilanguage },
  props: {
    deal: {
      type: Object,
      required: true,
    },
    businessUnit: {
      type: Object,
      required: true,
    },
    funnelBaseUrl: String,
    enableMultilanguage: Boolean, // feature flag
  },
  data() {
    return {
      showTranslations: false,
      activeCulture: null,
    }
  },
  computed: {
    toggleTranslationText() {
      const what = this.showTranslations ? 'Hide' : 'Show'
      return what + ' translations'
    },
    hasMultipleLanguages() {
      return (
        this.enableMultilanguage &&
        !!this.deal.TranslationInfo?.AvailableCultures?.length &&
        this.deal.Key !== null
      )
    },
    toggleTranslationIcon() {
      return this.showTranslations ? 'eye-crossed' : 'eye-open'
    },
    shouldShowMultilanguage() {
      return this.hasMultipleLanguages && this.showTranslations
    },
    translationModel() {
      if (!this.activeCulture) return null
      const key = this.activeCulture.Name
      return this.deal.TranslationInfo.Translations[key]
    },
    translationFields() {
      // todo: refactor to make it work with model field name instead of label
      // return this.deal.TranslationInfo.TranslatableFields
      return [
        'Name',
        'Description',
        'Image alt',
        'What do I get',
        'Program item title',
        'Program item subtitle',
      ]
    },
    nodesToHide() {
      return ['.deal-theme', '.deal-filters']
    },
    defaultCulture() {
      return this.deal.TranslationInfo ? this.deal.TranslationInfo.DefaultCulture : ''
    },
    availableCultures() {
      return this.deal.TranslationInfo?.AvailableCultures?.filter(
        c => c.Name !== this.defaultCulture.Name,
      )
    },
    defaultCultureDisplayName() {
      return this.defaultCulture ? this.defaultCulture.DisplayName : ''
    },
  },
  methods: {
    toggleTranslation() {
      this.showTranslations = !this.showTranslations
    },
    fillTranslationsFieldsForProgram() {
      if (typeof this.deal.TranslationInfo.AvailableCultures === 'undefined') return false
      for (const [key, availableCulture] of Object.entries(
        this.deal.TranslationInfo.AvailableCultures,
      )) {
        if (this.deal.TranslationInfo.Translations[availableCulture.Name].Program === null) {
          this.deal.TranslationInfo.Translations[
            availableCulture.Name
          ].Program = this.copyOfCleanTranslatableProgramObject()
        }
      }
    },
    copyOfCleanTranslatableProgramObject() {
      if (this.deal.Program === null) return null
      const programObject = clone(this.deal.Program)
      for (const [key, value] of Object.entries(programObject)) {
        programObject[key][0].title = ''
        programObject[key][0].description = ''
      }
      return programObject
    },
  },
  beforeMount() {
    if (this.hasMultipleLanguages) {
      this.activeCulture = this.availableCultures[0]
      completeTranslationInfo(this.deal)
      this.fillTranslationsFieldsForProgram()
    }
  },
}
</script>

<style scoped lang="stylus">
@import '~@v/variables.styl'

.online-tab-header
  display flex
  width 100%
  justify-content space-between

  .translation-button
    margin-bottom 20px

    .translation-icon
      margin-right space-XXS
</style>
